import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Loading } from '@components/ui/loading/loading';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RegulatoryDocumentationService } from '@6grain/masofa-monolith-api-client/dictionaries';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {CustomMessageService} from '@app/interceptors/custom-message';

export interface FileInfo {
  id?: string;
  fileName: string;
  fileSize: number;
  uploadedAt?: Date;
  fileStorageId?: string;
  fileFormat?: string;
  file?: File;
}

@Component({
  selector: 'app-document-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ToastModule,
    ButtonModule,
    TooltipModule,
    Loading
  ],
  providers: [
    {
      provide: MessageService,
      useClass: CustomMessageService
    },
  ],
  templateUrl: './document-file-upload.component.html',
  styleUrl: './document-file-upload.component.scss'
})
export class DocumentFileUploadComponent implements OnInit {
  @Input() fileStorageId?: string;
  @Output() fileSelected = new EventEmitter<FileInfo>();
  @Output() fileRemoved = new EventEmitter<void>();

  draggedFile: File | null = null;
  uploading = false;
  uploadProgress = 0;
  fileInfo: FileInfo | null = null;
  private localFile: File | null = null;
  initialLoading = false;

  constructor(
    private regulatoryDocService: RegulatoryDocumentationService,
    private messageService: MessageService,
    private http: HttpClient,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.fileStorageId) {
      this.initialLoading = true;
      this.loadFileInfo(this.fileStorageId).finally(() => this.initialLoading = false);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  private async handleFile(file: File): Promise<void> {
    try {
      this.localFile = file;

      this.fileInfo = {
        fileName: file.name,
        fileSize: file.size,
        uploadedAt: new Date(),
        fileFormat: this.getFileFormat(file.name),
        file: file
      };

      this.fileSelected.emit(this.fileInfo);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('Success')
      });
    } catch (error: any) {
      console.error('Error handling file:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('Error'),
        detail: error.message || this.translateService.instant('Error handling file')
      });
    }
  }

  private async loadFileInfo(fileStorageId: string): Promise<void> {
    try {
      const fileInfo = await firstValueFrom(
        this.regulatoryDocService.dictionariesRegulatoryDocumentationGetFileInfoIdGet(fileStorageId)
      );

      if (fileInfo) {
        const fileName = fileInfo.fileStoragePath?.split('/').pop() || 'Unknown file';
        this.fileInfo = {
          id: fileInfo.id,
          fileName: fileName,
          fileSize: (fileInfo as any).fileLength || 0,
          uploadedAt: fileInfo.createAt ? new Date(fileInfo.createAt) : undefined,
          fileStorageId: fileInfo.id,
          fileFormat: this.getFileFormatFromPath(fileName)
        };
      }
    } catch (error) {
      console.error('Error loading file info:', error);
    }
  }

  removeFile(): void {
    this.fileInfo = null;
    this.localFile = null;
    this.fileRemoved.emit();
  }

  getLocalFile(): File | null {
    return this.localFile;
  }

  downloadFile(): void {
    if (!this.fileInfo?.fileStorageId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('Error'),
        detail: this.translateService.instant('File ID not found')
      });
      return;
    }

    try {
      // Get base URL from API client configuration
      const baseUrl = this.regulatoryDocService.configuration.basePath || 'https://api.masofa.uz';
      const apiUrl = `${baseUrl}/common/FileStorageItem/GetFile/${this.fileInfo.fileStorageId}`;

      this.http.get(apiUrl, { responseType: 'blob' }).subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = this.fileInfo!.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success'),
            detail: this.translateService.instant('File downloaded successfully')
          });
        },
        error: (error) => {
          console.error('Error downloading file:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error'),
            detail: this.translateService.instant('Error downloading file')
          });
        }
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('Error'),
        detail: this.translateService.instant('Error downloading file')
      });
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  private getFileFormat(fileName: string): string {
    const ext = fileName.split('.').pop()?.toUpperCase() || '';
    return ext;
  }

  private getFileFormatFromPath(fileName: string): string {
    return this.getFileFormat(fileName);
  }
}

